import { useCallback, useEffect, useMemo, useState } from 'react';

import AdminPageShell from '../AdminPageShell';
import AdminToast from '../AdminToast';
import AdminPagination from '../AdminPagination';
import AdminTableDeleteButton from '../AdminTableDeleteButton';
import { useRegisterPortalRefresh } from '../../../contexts/PortalRefreshContext';
import { useAdminTablePagination } from '../../../hooks/useAdminTablePagination';
import { useAcademiaAdminDomaines, useAcademiaFormations } from '../../../hooks/useAcademiaAdmin';
import LucideSymbolIcon from '../../icons/LucideSymbolIcon';
import AcademiaSelect from '../../common/AcademiaSelect';
import {
  broadcastAdminCommunicationAlert,
  deleteAdminNotification,
  fetchAdminCommunicationModule11,
  runAdminPaymentDueAlerts,
} from '../../../services/academiaAdminService';
import { isAdministrateur } from '../../../utils/adminAccess';

import {
  AcademicPanelCard,
  AcademicPanelListItem,
  AcademicPanelTableWrap,
} from './AcademicPanelCard';

type Module11Data = {
  periodeJours?: number;
  canaux?: {
    pushMobile?: boolean;
    email?: boolean;
    sms?: boolean;
    smsLogDev?: boolean;
  };
  relancePaiement?: {
    impayesEligibles?: number;
    delaiJours?: number;
    cronIntervalSec?: number;
    notifications30j?: number;
  };
  volumes30j?: {
    totalNotifications?: number;
    pushDevicesActifs?: number;
    rappelsPaiement?: number;
    resultatsPublies?: number;
    notificationsBonus?: number;
    notificationsStages?: number;
    alertesAcademiques?: number;
  };
  couvertureFiche?: Record<string, boolean>;
  activiteRecente?: Array<{
    id: string;
    type: string;
    title: string;
    message: string;
    role: string;
    status: string;
    dateCreation: string;
  }>;
};

const FIELD_LABEL = 'text-xs font-bold text-on-surface-variant';
const FIELD_LABEL_BLOCK = 'block w-full min-w-0 text-sm';
const INPUT_CLASS =
  'w-full rounded-lg border border-outline-variant/30 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/15';
const BTN_PRIMARY = 'academic-panel-btn academic-panel-btn--primary w-full !justify-center';
const BTN_SECONDARY = 'academic-panel-btn academic-panel-btn--secondary w-full !justify-center';

type KpiTone = 'primary' | 'sky' | 'emerald' | 'amber';

const KPI_TONE_CLASS: Record<KpiTone, string> = {
  primary: 'border-outline-variant/25 bg-gradient-to-br from-white to-surface-container-low',
  sky: 'border-sky-200/50 bg-gradient-to-br from-sky-50/80 to-white',
  emerald: 'border-emerald-200/50 bg-gradient-to-br from-emerald-50/70 to-white',
  amber: 'border-amber-200/50 bg-gradient-to-br from-amber-50/70 to-white',
};

const KPI_LABEL_CLASS: Record<KpiTone, string> = {
  primary: 'text-on-surface-variant',
  sky: 'text-sky-800',
  emerald: 'text-emerald-800',
  amber: 'text-amber-800',
};

const KPI_VALUE_CLASS: Record<KpiTone, string> = {
  primary: 'text-primary',
  sky: 'text-sky-900',
  emerald: 'text-emerald-900',
  amber: 'text-amber-900',
};

function KpiTile({
  label,
  value,
  hint,
  icon,
  tone = 'primary',
}: {
  label: string;
  value: string | number;
  hint?: string;
  icon: string;
  tone?: KpiTone;
}) {
  return (
    <article className={`rounded-xl border p-4 shadow-sm ${KPI_TONE_CLASS[tone]}`}>
      <div className="flex items-start justify-between gap-3">
        <p className={`text-xs font-bold uppercase tracking-wide ${KPI_LABEL_CLASS[tone]}`}>{label}</p>
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/80 text-primary shadow-sm"
          aria-hidden
        >
          <LucideSymbolIcon name={icon} tone="inherit" size={18} />
        </span>
      </div>
      <p className={`mt-2 text-3xl font-black tabular-nums ${KPI_VALUE_CLASS[tone]}`}>{value}</p>
      {hint ? <p className={`mt-1 text-xs ${KPI_LABEL_CLASS[tone]}`}>{hint}</p> : null}
    </article>
  );
}

function StatusBadge({ active, activeLabel = 'Actif', inactiveLabel = 'Inactif' }: { active: boolean; activeLabel?: string; inactiveLabel?: string }) {
  return (
    <span
      className={
        active
          ? 'academic-panel-badge academic-panel-badge--success'
          : 'academic-panel-badge academic-panel-badge--muted'
      }
    >
      {active ? activeLabel : inactiveLabel}
    </span>
  );
}

function VolumePill({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest px-3 py-2.5 text-center">
      <p className="text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">{label}</p>
      <p className="mt-0.5 text-lg font-black tabular-nums text-slate-900">{value}</p>
    </div>
  );
}

type ToastState = { message: string; variant: 'success' | 'error' } | null;

type BroadcastScope = 'all' | 'domaine' | 'filiere' | 'cohorte';

const BROADCAST_SCOPE_OPTIONS: Array<{ value: BroadcastScope; label: string }> = [
  { value: 'all', label: 'Tous les apprenants et formateurs' },
  { value: 'domaine', label: 'Un domaine de formation' },
  { value: 'filiere', label: 'Une filière' },
  { value: 'cohorte', label: 'Une cohorte' },
];

export default function Module11CommunicationPanel({ user }: { user?: { role?: string } | null }) {
  const canDelete = isAdministrateur(user);
  const [data, setData] = useState<Module11Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [broadcastScope, setBroadcastScope] = useState<BroadcastScope>('all');
  const [targetDomaine, setTargetDomaine] = useState('');
  const [targetFiliereId, setTargetFiliereId] = useState('');
  const [targetCohorteId, setTargetCohorteId] = useState('');

  const { domaines } = useAcademiaAdminDomaines();
  const { filieres, cohortes } = useAcademiaFormations();

  const domaineLabelByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const domaine of domaines) {
      map.set(String(domaine.code).toUpperCase(), domaine.libelle ?? domaine.code);
    }
    return map;
  }, [domaines]);

  const cohortesForFiliere = useMemo(
    () => cohortes.filter((cohorte) => cohorte.idFiliere === targetFiliereId),
    [cohortes, targetFiliereId],
  );

  const broadcastScopeHint = useMemo(() => {
    if (broadcastScope === 'all') {
      return 'Diffusion à l’ensemble des apprenants et formateurs actifs sur la plateforme.';
    }
    if (broadcastScope === 'domaine' && targetDomaine) {
      const label = domaineLabelByCode.get(targetDomaine.toUpperCase()) ?? targetDomaine;
      return `Cible : apprenants inscrits et formateurs du domaine « ${label} ».`;
    }
    if (broadcastScope === 'filiere' && targetFiliereId) {
      const filiere = filieres.find((row) => row.id === targetFiliereId);
      const label = filiere ? `${filiere.code ?? ''} — ${filiere.libelle ?? ''}`.trim() : targetFiliereId;
      return `Cible : apprenants inscrits et formateurs de la filière « ${label} ».`;
    }
    if (broadcastScope === 'cohorte' && targetCohorteId) {
      const cohorte = cohortes.find((row) => row.id === targetCohorteId);
      const label = cohorte ? `${cohorte.code ?? ''} — ${cohorte.libelle ?? ''}`.trim() : targetCohorteId;
      return `Cible : apprenants inscrits et formateurs de la cohorte « ${label} ».`;
    }
    return 'Sélectionnez la portée de diffusion ci-dessous.';
  }, [
    broadcastScope,
    cohortes,
    domaineLabelByCode,
    filieres,
    targetCohorteId,
    targetDomaine,
    targetFiliereId,
  ]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await fetchAdminCommunicationModule11();
      setData(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  useRegisterPortalRefresh(load);
  useEffect(() => {
    void load();
  }, [load]);

  const handleRelances = async () => {
    setBusy(true);
    try {
      const r = await runAdminPaymentDueAlerts();
      showToast(
        `${r?.sent ?? 0} relance(s) envoyée(s) — ${r?.emailsSent ?? 0} e-mail(s), ${r?.smsSent ?? 0} SMS.`,
        'success',
      );
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Impossible d’envoyer les relances.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleBroadcast = async () => {
    if (!title.trim() || !message.trim()) {
      showToast('Titre et message requis.', 'error');
      return;
    }
    if (broadcastScope === 'domaine' && !targetDomaine) {
      showToast('Sélectionnez un domaine de formation.', 'error');
      return;
    }
    if (broadcastScope === 'filiere' && !targetFiliereId) {
      showToast('Sélectionnez une filière.', 'error');
      return;
    }
    if (broadcastScope === 'cohorte' && (!targetFiliereId || !targetCohorteId)) {
      showToast('Sélectionnez une filière et une cohorte.', 'error');
      return;
    }
    setBusy(true);
    try {
      const payload: {
        title: string;
        message: string;
        roles: string[];
        sendEmail: boolean;
        domaine?: string;
        idFiliere?: string;
        idCohorte?: string;
      } = {
        title: title.trim(),
        message: message.trim(),
        roles: ['APPRENANT', 'FORMATEUR'],
        sendEmail,
      };
      if (broadcastScope === 'domaine') payload.domaine = targetDomaine;
      if (broadcastScope === 'filiere') payload.idFiliere = targetFiliereId;
      if (broadcastScope === 'cohorte') payload.idCohorte = targetCohorteId;

      const r = await broadcastAdminCommunicationAlert(payload);
      showToast(r?.message ?? 'Alerte diffusée.', 'success');
      setTitle('');
      setMessage('');
      setBroadcastScope('all');
      setTargetDomaine('');
      setTargetFiliereId('');
      setTargetCohorteId('');
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Impossible de diffuser l’alerte.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const v = data?.volumes30j;
  const canaux = data?.canaux;
  const relance = data?.relancePaiement;
  const jours = data?.periodeJours ?? 30;
  const canauxActifs = [
    canaux?.pushMobile ? 'Push' : null,
    canaux?.email ? 'E-mail' : null,
  ].filter(Boolean);

  const activiteRecente = data?.activiteRecente ?? [];
  const {
    paginatedItems: paginatedActivite,
    currentPage: activitePage,
    totalPages: activitePages,
    onPageChange: onActivitePageChange,
    summary: activiteSummary,
  } = useAdminTablePagination(activiteRecente);

  return (
    <>
    <AdminPageShell
      title="Communication & notifications"
      description="Notifications mobiles, rappels de paiement, résultats, bonus, stages et alertes académiques."
      sideCard={{
        label: 'Notifications (30 j)',
        value: loading ? '…' : String(v?.totalNotifications ?? 0),
        helper: canauxActifs.length ? canauxActifs.join(' · ') : 'Canaux en cours de configuration',
        sideLinks: [
          { label: 'Paiements', to: '/admin/paiements', icon: 'account_balance' },
          { label: 'Excellence', to: '/admin/excellence', icon: 'emoji_events' },
        ],
      }}
      isLoading={loading && !data}
      isError={Boolean(error)}
      errorMessage={error ?? undefined}
      onRetry={() => void load()}
      loadingMessage="Chargement de la communication…"
    >
      {data ? (
        <div className="space-y-6">
          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <KpiTile
              label={`Notifications (${jours} j)`}
              value={v?.totalNotifications ?? 0}
              icon="notifications_active"
              tone="primary"
            />
            <KpiTile
              label="Appareils push actifs"
              value={v?.pushDevicesActifs ?? 0}
              icon="phone_iphone"
              tone="sky"
            />
            <KpiTile
              label="Rappels paiement"
              value={v?.rappelsPaiement ?? 0}
              hint={`${jours} derniers jours`}
              icon="schedule"
              tone="emerald"
            />
            <KpiTile
              label="Impayés éligibles"
              value={relance?.impayesEligibles ?? 0}
              hint={`Délai ≥ ${relance?.delaiJours ?? 3} jours`}
              icon="account_balance_wallet"
              tone="amber"
            />
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <AcademicPanelCard
              icon={<LucideSymbolIcon name="campaign" tone="primary" size={22} />}
              title="Canaux actifs"
              subtitle="État des moyens de contact disponibles sur la plateforme"
            >
              <ul className="academic-panel-list space-y-2">
                <AcademicPanelListItem>
                  <span className="flex items-center gap-2 font-medium text-slate-900">
                    <LucideSymbolIcon name="notifications" tone="primary" size={18} />
                    Push mobile
                  </span>
                  <StatusBadge active={Boolean(canaux?.pushMobile)} />
                </AcademicPanelListItem>
                <AcademicPanelListItem>
                  <span className="flex items-center gap-2 font-medium text-slate-900">
                    <LucideSymbolIcon name="mail" tone="primary" size={18} />
                    E-mail transactionnel
                  </span>
                  <StatusBadge
                    active={Boolean(canaux?.email)}
                    activeLabel="Configuré"
                    inactiveLabel="Non configuré"
                  />
                </AcademicPanelListItem>
              </ul>
            </AcademicPanelCard>

            <AcademicPanelCard
              variant="success"
              icon={<LucideSymbolIcon name="verified" tone="inherit" size={22} />}
              title="Fonctionnalités activées"
              subtitle="Services de notification disponibles pour votre établissement"
            >
              <div className="grid gap-2 sm:grid-cols-2">
                {[
                  ['Notifications mobiles', data?.couvertureFiche?.notificationsMobiles],
                  ['Rappels de paiement', data?.couvertureFiche?.rappelsPaiement],
                  ['Publication des résultats', data?.couvertureFiche?.publicationResultats],
                  ['Notifications des bonus', data?.couvertureFiche?.notificationsBonus],
                  ['Notifications des stages', data?.couvertureFiche?.notificationsStages],
                  ['Alertes académiques', data?.couvertureFiche?.alertesAcademiquesAdministratives],
                ].map(([label, ok]) => (
                  <div
                    key={String(label)}
                    className={[
                      'flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm font-medium',
                      ok
                        ? 'border-emerald-200/60 bg-emerald-50/40 text-emerald-950'
                        : 'border-outline-variant/25 bg-surface-container-lowest text-on-surface-variant',
                    ].join(' ')}
                  >
                    <LucideSymbolIcon
                      name={ok ? 'check_circle' : 'pending'}
                      size={16}
                      tone={ok ? 'primary' : 'muted'}
                    />
                    {label}
                  </div>
                ))}
              </div>
            </AcademicPanelCard>
          </section>

          <section className="grid gap-4 lg:grid-cols-2">
            <AcademicPanelCard
              variant="accent"
              icon={<LucideSymbolIcon name="payments" tone="inherit" size={22} />}
              title="Rappels de paiement"
              subtitle={`Volumes des ${jours} derniers jours et relance manuelle des impayés`}
              footer={
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void handleRelances()}
                  className={`${BTN_PRIMARY} disabled:opacity-50`}
                >
                  <LucideSymbolIcon name="send" tone="inherit" size={16} className="text-white" />
                  {busy ? 'Envoi en cours…' : 'Lancer les rappels de paiement'}
                </button>
              }
            >
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <VolumePill label="Résultats" value={v?.resultatsPublies ?? 0} />
                <VolumePill label="Bonus" value={v?.notificationsBonus ?? 0} />
                <VolumePill label="Stages" value={v?.notificationsStages ?? 0} />
                <VolumePill label="Alertes" value={v?.alertesAcademiques ?? 0} />
              </div>
            </AcademicPanelCard>

            <AcademicPanelCard
              variant="accent"
              icon={<LucideSymbolIcon name="campaign" tone="inherit" size={22} />}
              title="Diffuser une alerte académique"
              subtitle="Notification push ciblée ou globale, avec e-mail optionnel"
              footer={
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void handleBroadcast()}
                  className={`${BTN_SECONDARY} disabled:opacity-50`}
                >
                  <LucideSymbolIcon name="campaign" tone="inherit" size={16} />
                  {busy ? 'Diffusion…' : "Diffuser l'alerte"}
                </button>
              }
            >
              <div className="space-y-3">
                <label className={FIELD_LABEL_BLOCK}>
                  <span className={FIELD_LABEL}>Portée de diffusion</span>
                  <AcademiaSelect
                    className="mt-1"
                    value={broadcastScope}
                    onChange={(e) => {
                      const nextScope = e.target.value as BroadcastScope;
                      setBroadcastScope(nextScope);
                      setTargetDomaine('');
                      setTargetFiliereId('');
                      setTargetCohorteId('');
                    }}
                  >
                    {BROADCAST_SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </AcademiaSelect>
                </label>

                {broadcastScope === 'domaine' ? (
                  <label className={FIELD_LABEL_BLOCK}>
                    <span className={FIELD_LABEL}>Domaine</span>
                    <AcademiaSelect
                      className="mt-1"
                      value={targetDomaine}
                      onChange={(e) => setTargetDomaine(e.target.value)}
                    >
                      <option value="">Choisir un domaine…</option>
                      {domaines.map((domaine) => (
                        <option key={domaine.id} value={domaine.code}>
                          {domaine.libelle ?? domaine.code}
                        </option>
                      ))}
                    </AcademiaSelect>
                  </label>
                ) : null}

                {broadcastScope === 'filiere' ? (
                  <label className={FIELD_LABEL_BLOCK}>
                    <span className={FIELD_LABEL}>Filière</span>
                    <AcademiaSelect
                      className="mt-1"
                      value={targetFiliereId}
                      onChange={(e) => setTargetFiliereId(e.target.value)}
                    >
                      <option value="">Choisir une filière…</option>
                      {filieres.map((filiere) => (
                        <option key={filiere.id} value={filiere.id}>
                          {filiere.code} — {filiere.libelle}
                        </option>
                      ))}
                    </AcademiaSelect>
                  </label>
                ) : null}

                {broadcastScope === 'cohorte' ? (
                  <>
                    <label className={FIELD_LABEL_BLOCK}>
                      <span className={FIELD_LABEL}>Filière</span>
                      <AcademiaSelect
                        className="mt-1"
                        value={targetFiliereId}
                        onChange={(e) => {
                          setTargetFiliereId(e.target.value);
                          setTargetCohorteId('');
                        }}
                      >
                        <option value="">Choisir une filière…</option>
                        {filieres.map((filiere) => (
                          <option key={filiere.id} value={filiere.id}>
                            {filiere.code} — {filiere.libelle}
                          </option>
                        ))}
                      </AcademiaSelect>
                    </label>
                    <label className={FIELD_LABEL_BLOCK}>
                      <span className={FIELD_LABEL}>Cohorte</span>
                      <AcademiaSelect
                        className="mt-1"
                        value={targetCohorteId}
                        onChange={(e) => setTargetCohorteId(e.target.value)}
                        disabled={!targetFiliereId}
                      >
                        <option value="">
                          {targetFiliereId ? 'Choisir une cohorte…' : 'Sélectionnez d’abord une filière'}
                        </option>
                        {cohortesForFiliere.map((cohorte) => (
                          <option key={cohorte.id} value={cohorte.id}>
                            {cohorte.code} — {cohorte.libelle}
                          </option>
                        ))}
                      </AcademiaSelect>
                    </label>
                  </>
                ) : null}

                <p className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest px-3 py-2.5 text-xs text-on-surface-variant">
                  {broadcastScopeHint}
                </p>

                <label className={FIELD_LABEL_BLOCK}>
                  <span className={FIELD_LABEL}>Titre de l&apos;alerte</span>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Ex. Report de session, convocation…"
                    className={`mt-1 ${INPUT_CLASS}`}
                  />
                </label>
                <label className={FIELD_LABEL_BLOCK}>
                  <span className={FIELD_LABEL}>Message</span>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Contenu du message aux apprenants et formateurs"
                    rows={4}
                    className={`mt-1 ${INPUT_CLASS}`}
                  />
                </label>
                <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-outline-variant/20 bg-surface-container-lowest px-3 py-2.5 text-sm">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="h-4 w-4 rounded border-outline-variant/40 text-primary focus:ring-primary/30"
                  />
                  <span className="font-medium text-slate-900">Envoyer aussi par e-mail</span>
                </label>
              </div>
            </AcademicPanelCard>
          </section>

          <AcademicPanelCard
            flush
            icon={<LucideSymbolIcon name="history" tone="primary" size={22} />}
            title="Activité récente"
            subtitle="Toutes les notifications enregistrées sur la plateforme (plus récentes en premier)"
          >
            <AcademicPanelTableWrap
              empty={
                activiteRecente.length === 0 ? (
                  <p className="text-sm text-on-surface-variant">Aucune notification enregistrée.</p>
                ) : undefined
              }
            >
              {activiteRecente.length > 0 ? (
                <table className="academic-panel-table">
                  <thead>
                    <tr>
                      <th>Notification</th>
                      <th className="hidden sm:table-cell">Message</th>
                      <th className="text-right">Date</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedActivite.map((row) => (
                      <tr key={row.id}>
                        <td className="font-semibold text-slate-900">{row.title}</td>
                        <td className="hidden max-w-md truncate sm:table-cell text-on-surface-variant">
                          {row.message}
                        </td>
                        <td className="whitespace-nowrap text-right text-xs text-on-surface-variant">
                          {new Date(row.dateCreation).toLocaleString('fr-FR')}
                        </td>
                        <td className="text-right">
                          <AdminTableDeleteButton
                            canDelete={canDelete}
                            disabled={deletingId === row.id}
                            confirmMessage={`Supprimer définitivement la notification « ${row.title} » ?`}
                            onDelete={async () => {
                              setDeletingId(row.id);
                              try {
                                await deleteAdminNotification(row.id);
                                showToast('Notification supprimée.', 'success');
                                await load();
                              } catch (e) {
                                showToast(
                                  e instanceof Error ? e.message : 'Suppression impossible.',
                                  'error',
                                );
                              } finally {
                                setDeletingId(null);
                              }
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </AcademicPanelTableWrap>
            {activiteRecente.length > 0 ? (
              <AdminPagination
                className="border-t border-outline-variant/15 px-4 py-3"
                currentPage={activitePage}
                totalPages={activitePages}
                onPageChange={onActivitePageChange}
                summary={activiteSummary}
              />
            ) : null}
          </AcademicPanelCard>
        </div>
      ) : null}
    </AdminPageShell>
    <AdminToast message={toast?.message ?? null} variant={toast?.variant ?? 'success'} onClose={dismissToast} />
    </>
  );
}
